import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnrollRegister = () => {
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isLogin, setIsLogin] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    country: localStorage.getItem('user_country') || 'IN',
    city: '',
    howHeard: ''
  });

  useEffect(() => {
    const pkg = localStorage.getItem('selected_package');
    if (pkg) {
      setSelectedPackage(JSON.parse(pkg));
    } else {
      navigate('/enroll');
    }
    
    // Check if already logged in
    const token = localStorage.getItem('enroll_token');
    if (token) {
      navigate('/enroll/consent');
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/enroll/register`, null, {
        params: {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          phone: formData.phone,
          country: formData.country,
          city: formData.city,
          how_heard: formData.howHeard
        }
      });
      
      localStorage.setItem('enroll_token', response.data.access_token);
      localStorage.setItem('enroll_parent', JSON.stringify(response.data.parent));
      navigate('/enroll/consent');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/enroll/login`, null, {
        params: {
          email: formData.email,
          password: formData.password
        }
      });
      
      localStorage.setItem('enroll_token', response.data.access_token);
      localStorage.setItem('enroll_parent', JSON.stringify(response.data.parent));
      
      // Check enrollment status and redirect accordingly
      const status = response.data.parent.enrollment_status;
      if (status === 'active') {
        navigate('/parent/dashboard');
      } else if (status === 'consent_given') {
        navigate('/enroll/child');
      } else if (status === 'child_added') {
        navigate('/enroll/payment');
      } else {
        navigate('/enroll/consent');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const howHeardOptions = [
    'Google Search',
    'Social Media',
    'Friend/Family Referral',
    'School Recommendation',
    'Blog/Article',
    'Other'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Navigation />
      
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-xl mx-auto">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold">1</div>
              <span className="text-sm font-medium text-orange-600">Package</span>
            </div>
            <div className="w-8 h-0.5 bg-orange-500"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold">2</div>
              <span className="text-sm font-medium text-orange-600">Account</span>
            </div>
            <div className="w-8 h-0.5 bg-gray-300"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center text-sm">3</div>
              <span className="text-sm text-gray-400">Consent</span>
            </div>
            <div className="w-8 h-0.5 bg-gray-300"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center text-sm">4</div>
              <span className="text-sm text-gray-400">Child</span>
            </div>
          </div>

          {/* Selected Package Summary */}
          {selectedPackage && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-orange-600 font-medium">Selected Plan</p>
                  <p className="text-lg font-bold text-gray-900">{selectedPackage.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-orange-600">{selectedPackage.price_display}</p>
                  {!selectedPackage.is_trial && <p className="text-sm text-gray-500">/month</p>}
                </div>
              </div>
            </div>
          )}

          <Card className="border-2 border-blue-200 shadow-xl">
            <CardContent className="p-8">
              {/* Toggle Login/Register */}
              <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
                <button
                  onClick={() => setIsLogin(false)}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    !isLogin ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                  }`}
                >
                  Create Account
                </button>
                <button
                  onClick={() => setIsLogin(true)}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    isLogin ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                  }`}
                >
                  Login
                </button>
              </div>

              <form onSubmit={isLogin ? handleLogin : handleRegister} className="space-y-4">
                {!isLogin && (
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required={!isLogin}
                      className="mt-1"
                    />
                  </div>
                )}

                <div>
                  <Label>Email Address *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className="mt-1"
                  />
                </div>

                {!isLogin && (
                  <div>
                    <Label>Phone Number *</Label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      required={!isLogin}
                      className="mt-1"
                    />
                  </div>
                )}

                <div>
                  <Label>Password *</Label>
                  <Input
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    className="mt-1"
                  />
                </div>

                {!isLogin && (
                  <>
                    <div>
                      <Label>Confirm Password *</Label>
                      <Input
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="••••••••"
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>City</Label>
                      <Input
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="Your city"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>How did you hear about us?</Label>
                      <select
                        name="howHeard"
                        value={formData.howHeard}
                        onChange={handleChange}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">Select an option</option>
                        {howHeardOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <div className="pt-4 flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/enroll')}
                    className="px-6"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6"
                  >
                    {loading ? 'Processing...' : isLogin ? 'Login & Continue' : 'Create Account'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EnrollRegister;
